import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { TRY, NUM, unitLabel } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, Search, AlertTriangle } from "lucide-react";

const SORTS = [["value", "Eksik değerine göre"], ["qty", "Eksi adede göre"], ["brand", "Markaya göre"]];

/** Eksi stoktaki ürünler — marka bazlı özet + ayrıntılı liste (stok değerine sıfır sayılır). */
export default function NegativeStockList() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [brand, setBrand] = useState("");
  const [sort, setSort] = useState("value");
  useEffect(() => { api.get("/products/stock-detail?negative=true").then((r) => setItems(r.data.items || [])).catch(() => setItems([])); }, []);

  const groups = useMemo(() => {
    const m = {};
    (items || []).forEach((p) => { const g = (m[p.brand] ||= { brand: p.brand, count: 0, value: 0, qty: 0 }); g.count += 1; g.value += p.value; g.qty += -p.quantity; });
    return Object.values(m).sort((a, b) => b.value - a.value || b.count - a.count);
  }, [items]);
  const rows = useMemo(() => {
    const s = q.trim().toLocaleLowerCase("tr");
    const list = (items || []).filter((p) => (!brand || p.brand === brand) && (!s || `${p.name} ${p.code || ""} ${p.brand}`.toLocaleLowerCase("tr").includes(s)));
    const cmp = { value: (a, b) => b.value - a.value, qty: (a, b) => a.quantity - b.quantity, brand: (a, b) => a.brand.localeCompare(b.brand, "tr") || b.value - a.value }[sort];
    return [...list].sort(cmp);
  }, [items, q, brand, sort]);
  const sum = rows.reduce((s, p) => s + p.value, 0);

  if (items === null) return <div className="p-6 flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Eksi stoklar yükleniyor…</div>;
  if (!items.length) return <div className="p-6 text-sm text-muted-foreground" data-testid="neg-stock-empty">Eksi stokta ürün yok.</div>;
  return (
    <div data-testid="neg-stock-panel">
      <div className="px-4 pb-3 text-xs text-amber-800 flex items-start gap-1.5"><AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />Eksi stoklar Stok Değeri'ne <b>sıfır</b> sayılır. Aşağıdaki tutar, eksik miktarların maliyetidir (sipariş verilince tamamlanacak).</div>
      <div className="px-4 pb-3 flex gap-1.5 flex-wrap" data-testid="neg-stock-brands">
        <button type="button" onClick={() => setBrand("")} className={cn("rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors", !brand ? "bg-[#0F2745] text-white border-[#0F2745]" : "bg-card hover:bg-muted")} data-testid="neg-brand-all">Tüm markalar · {NUM(items.length)}</button>
        {groups.map((g) => (
          <button key={g.brand} type="button" onClick={() => setBrand(brand === g.brand ? "" : g.brand)} className={cn("rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors", brand === g.brand ? "bg-red-600 text-white border-red-600" : "bg-red-50 text-red-800 border-red-200 hover:bg-red-100")} data-testid={`neg-brand-${g.brand}`}>
            {g.brand} · {NUM(g.count)} ürün · {TRY(g.value)}
          </button>
        ))}
      </div>
      <div className="px-4 pb-3 flex flex-wrap items-center gap-2">
        <div className="relative w-full sm:w-72"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" /><Input placeholder="Ürün / kod ara..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 h-9" data-testid="neg-stock-search" /></div>
        <div className="inline-flex gap-1 bg-muted/60 rounded-lg p-1">
          {SORTS.map(([k, l]) => <Button key={k} size="sm" variant="ghost" onClick={() => setSort(k)} className={cn("h-7 text-xs", sort === k && "bg-card shadow-sm")} data-testid={`neg-sort-${k}`}>{l}</Button>)}
        </div>
      </div>
      <div className="overflow-x-auto max-h-[460px] overflow-y-auto border-t">
        <table className="w-full text-sm min-w-[720px]" data-testid="neg-stock-table">
          <thead className="sticky top-0 bg-muted/80 backdrop-blur text-[11px] uppercase tracking-wide text-muted-foreground">
            <tr><th className="px-3 py-2 text-left w-10">#</th><th className="px-3 py-2 text-left">Ürün</th><th className="px-3 py-2 text-left">Marka</th><th className="px-3 py-2 text-right">Stok</th><th className="px-3 py-2 text-right">Birim Maliyet</th><th className="px-3 py-2 text-right">Eksik Değeri</th></tr>
          </thead>
          <tbody>
            {rows.map((p, i) => (
              <tr key={p.id} className="border-t hover:bg-muted/40" data-testid={`neg-row-${p.id}`}>
                <td className="px-3 py-1.5 text-muted-foreground tabular-nums">{i + 1}</td>
                <td className="px-3 py-1.5"><div className="font-semibold">{p.name}</div>{p.code && <div className="text-[11px] font-mono text-muted-foreground">{p.code}</div>}</td>
                <td className="px-3 py-1.5 text-xs font-semibold">{p.brand}</td>
                <td className="px-3 py-1.5 text-right tabular-nums font-bold text-red-600 whitespace-nowrap">{NUM(p.quantity)} {unitLabel(p.unit, "ADET")}</td>
                <td className="px-3 py-1.5 text-right tabular-nums whitespace-nowrap">{TRY(p.cost || 0)}</td>
                <td className="px-3 py-1.5 text-right tabular-nums font-bold whitespace-nowrap">{TRY(p.value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="px-4 py-2.5 border-t bg-red-50/60 flex flex-wrap items-center justify-end gap-3 text-sm">
        <span className="text-muted-foreground">{NUM(rows.length)} ürün · toplam eksik maliyeti:</span>
        <span className="font-bold text-red-700 tabular-nums" data-testid="neg-stock-total">{TRY(sum)}</span>
      </div>
    </div>
  );
}
